import { useState, useCallback, useRef, useEffect } from 'react';
import {
  FaStethoscope, FaSync, FaSearch, FaExclamationTriangle, FaCheckCircle,
  FaTimesCircle, FaHeartbeat, FaPlay, FaSpinner, FaChevronDown, FaChevronRight,
  FaKey, FaHistory,
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

/**
 * NBN service diagnostics + Service Health Summary, over the Vocus Wholesale REST API.
 *
 * Every test is asynchronous: POST returns an id, then we poll until the state settles.
 * Vocus discards results without notice, so what is on screen is all there is — there is
 * no history to come back to.
 */

const TONES = {
  green: 'bg-[#98971a]/20 text-[#b8bb26] border-[#98971a]/50',
  amber: 'bg-[#d79921]/20 text-[#fabd2f] border-[#d79921]/50',
  red:   'bg-[#cc241d]/20 text-[#fb4934] border-[#cc241d]/50',
  grey:  'bg-[#504945]/40 text-[#a89984] border-[#665c54]/60',
  blue:  'bg-[#458588]/20 text-[#83a598] border-[#458588]/50',
};

const TECHS = ['FTTP', 'FTTN', 'FTTB', 'FTTC', 'HFC', 'FW'];

// Vocus returns the SHS indicators as literal colour words.
const indicatorTone = (v) => {
  const s = String(v || '').toLowerCase();
  if (s === 'green') return 'green';
  if (s === 'yellow' || s === 'amber') return 'amber';
  if (s === 'red') return 'red';
  return 'grey';
};

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const token = () => localStorage.getItem('adminToken');

async function callDiagnostics(payload) {
  const res = await fetch(API_ENDPOINTS.VOCUS_DIAGNOSTICS, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || `Request failed (HTTP ${res.status})`);
    err.payload = data;
    err.statusCode = res.status;
    throw err;
  }
  return data;
}

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtDuration = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
};

// Vocus sends booleans as the strings "true"/"false".
const fmtBool = (v) => (v == null ? null : (/^false$/i.test(v) ? 'No' : /^true$/i.test(v) ? 'Yes' : v));

const verdictTone = (v) => {
  const s = String(v || '').toLowerCase();
  if (s.includes('pass')) return 'green';
  if (s.includes('fail')) return 'red';
  return 'grey';
};

const joinPort = (h) => [h.port, h.portState].filter(Boolean).join(' · ') || null;

// Only the windows an operator reasons about. The full 5x4 matrix stays in the group card.
const fmtDropouts = (h) => {
  const parts = [
    h.dropoutsToday != null ? `${h.dropoutsToday} today` : null,
    h.dropouts7d != null ? `${h.dropouts7d} / 7d` : null,
    h.dropouts30d != null ? `${h.dropouts30d} / 30d` : null,
  ].filter(Boolean);
  return parts.length ? parts.join('  ') : null;
};

const hasHeadline = (h) => h && Object.values(h).some((v) => v != null && v !== '');

function Fact({ label, value, tone }) {
  if (value == null || value === '') return null;
  return (
    <div className="min-w-0">
      <div className="text-[11px] text-[#928374] uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-medium ${tone === 'green' ? 'text-[#b8bb26]' : tone === 'red' ? 'text-[#fb4934]' : 'text-[#ebdbb2]'}`}>
        {value}
      </div>
    </div>
  );
}

/** One of Vocus's result categories. Long ones start collapsed — the 5x4 dropout matrix
 *  is only worth opening when you are chasing an intermittent fault. */
function GroupCard({ title, subtitle, items, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2 bg-[#282828] border border-[#3c3836] rounded">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#32302f] transition-colors"
      >
        {open ? <FaChevronDown className="text-[10px] text-[#928374]" /> : <FaChevronRight className="text-[10px] text-[#928374]" />}
        <span className="text-sm font-medium text-[#ebdbb2]">{title}</span>
        {subtitle && <span className="text-xs text-[#928374]">{subtitle}</span>}
        <span className="text-xs text-[#928374] ml-auto">{items.length}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2">
          {items.map((it, i) => (
            <div key={`${it.name}-${i}`} className="min-w-0">
              <div className="text-[11px] text-[#928374] truncate" title={it.name}>{it.name}</div>
              <div className="text-sm text-[#ebdbb2] font-mono break-all">{it.value || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** OPER log records: a list of flat objects whose columns Vocus decides, so derive them. */
function RecordTable({ records }) {
  const cols = [];
  for (const r of records || []) {
    for (const k of Object.keys(r || {})) if (!cols.includes(k)) cols.push(k);
  }
  if (cols.length === 0) return null;
  return (
    <div className="bg-[#282828] border border-[#3c3836] rounded overflow-auto max-h-96">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-[#32302f]">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left px-3 py-2 text-[11px] uppercase tracking-wide text-[#928374] font-medium whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={i} className="border-t border-[#3c3836]">
              {cols.map((c) => (
                <td key={c} className="px-3 py-1.5 text-[#ebdbb2] font-mono text-xs whitespace-nowrap">
                  {r[c] == null || r[c] === '' ? '—' : String(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Badge({ tone = 'grey', children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-medium ${TONES[tone]}`}>
      {children}
    </span>
  );
}

/** Vocus nests results arbitrarily deep as {name, valueType, value}. Render it as a tree. */
function MeasureTree({ node, depth = 0 }) {
  if (node == null) return null;
  if (Array.isArray(node)) {
    return node.map((n, i) => <MeasureTree key={i} node={n} depth={depth} />);
  }
  if (typeof node !== 'object') {
    return <span className="text-[#ebdbb2]">{String(node)}</span>;
  }

  const label = node.name || node.metricName || node.entityType || null;
  const value = node.value;
  const isLeaf = value == null || typeof value !== 'object';

  return (
    <div style={{ marginLeft: depth ? 14 : 0 }} className={depth ? 'border-l border-[#3c3836] pl-3 py-0.5' : 'py-0.5'}>
      {isLeaf ? (
        <div className="flex gap-2 text-sm">
          {label && <span className="text-[#a89984] shrink-0">{label}:</span>}
          <span className="text-[#ebdbb2] break-all">{value == null ? '—' : String(value)}</span>
        </div>
      ) : (
        <>
          {label && <div className="text-sm text-[#a89984] font-medium">{label}</div>}
          <MeasureTree node={value} depth={depth + 1} />
        </>
      )}
      {Array.isArray(node.testMeasure) && <MeasureTree node={node.testMeasure} depth={depth + 1} />}
    </div>
  );
}

export default function ServiceDiagnostics({ initialServiceId = '', initialTech = '' }) {
  const [serviceId, setServiceId] = useState(initialServiceId);
  const [tech, setTech] = useState(initialTech);
  const [tests, setTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [error, setError] = useState(null);

  const [run, setRun] = useState(null);   // { kind, id, state, label, complete, testMeasure, summary, startedAt }
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null); // a destructive test awaiting confirmation
  const [detection, setDetection] = useState(null);   // what Vocus's reply implies about the technology
  const [showRaw, setShowRaw] = useState(false);

  const pollRef = useRef(null);
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  const loadTests = useCallback(async () => {
    setError(null);
    setLoadingTests(true);
    try {
      const res = await callDiagnostics({ mode: 'LIST_TESTS', accessTechnology: tech || undefined });
      setTests(res.tests || []);
      if (tech && !res.accessTechnologyRecognised) {
        setError(`'${tech}' is not a technology Vocus recognises — showing every test in the catalogue.`);
      }
    } catch (e) {
      setError(e.message);
      setTests([]);
    } finally {
      setLoadingTests(false);
    }
  }, [tech]);

  useEffect(() => { loadTests(); }, [loadTests]);

  const poll = useCallback((kind, id, label) => {
    const startedAt = Date.now();
    stopPolling();
    pollRef.current = setInterval(async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        stopPolling();
        setRun((r) => (r ? { ...r, stalled: true } : r));
        return;
      }
      try {
        const res = kind === 'health'
          ? await callDiagnostics({ mode: 'GET_HEALTH', serviceId, healthId: id })
          : await callDiagnostics({ mode: 'GET_TEST', serviceId, testId: id });
        setRun({ kind, id, label, startedAt, ...res });
        // Vocus's own reply says what the access technology is, so narrow the menu
        // rather than making the operator know. Only act on an unambiguous match; a
        // wrong narrowing hides the test they actually came for.
        if (res.detected) {
          setDetection(res.detected);
          if (res.detected.tech) setTech((cur) => (cur === res.detected.tech ? cur : res.detected.tech));
        }
        if (res.complete) stopPolling();
      } catch (e) {
        stopPolling();
        setRun((r) => (r ? { ...r, pollError: e.message } : r));
      }
    }, POLL_INTERVAL_MS);
  }, [serviceId, stopPolling]);

  const startTest = useCallback(async (test, confirmed = false) => {
    if (!serviceId.trim()) { setError('Enter the Vocus NBN Service ID first.'); return; }
    if (test.destructive && !confirmed) { setConfirming(test); return; }

    setConfirming(null);
    setError(null);
    setBusy(true);
    stopPolling();
    setRun(null);
    setDetection(null);
    try {
      const res = await callDiagnostics({
        mode: 'RUN_TEST',
        serviceId: serviceId.trim(),
        testCode: test.code,
        accessTechnology: tech || undefined,
        ...(test.destructive ? { confirmDestructive: true } : {}),
      });
      setRun({ kind: 'test', id: res.testId, label: test.name, state: res.state, complete: false, startedAt: Date.now() });
      poll('test', res.testId, test.name);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [serviceId, tech, poll, stopPolling]);

  /**
   * OPER log lookups (AUTH-LOG / SESSION-LOG). These are SOAP, and per OPER.pdf §2.4 they
   * are SYNCHRONOUS — the records come back on the first call, so unlike the REST
   * diagnostics there is nothing to poll.
   */
  const runOperLog = useCallback(async (kind) => {
    if (!serviceId.trim()) { setError('Enter the Vocus NBN Service ID first.'); return; }
    setError(null);
    setBusy(true);
    stopPolling();
    setRun(null);
    setDetection(null);
    try {
      const res = await callDiagnostics({
        mode: kind === 'auth' ? 'AUTH_LOG' : 'SESSION_LOG',
        serviceId: serviceId.trim(),
      });
      setRun({
        kind: kind === 'auth' ? 'authlog' : 'sessionlog',
        label: kind === 'auth' ? 'Authentication log' : 'Session log',
        id: null, state: 'completed', complete: true, startedAt: Date.now(), ...res,
      });
    } catch (e) {
      // Vocus has not enabled ProductID OPER on our AccessKey, so these two are dead until
      // they do. Say that, rather than letting it read as a transient failure someone
      // might retry all afternoon.
      setError(e.payload?.errorCode === 'OPER_NOT_ENTITLED'
        ? `${e.message} The line tests and Health Summary above are unaffected — they use a different Vocus API.`
        : e.message);
    } finally {
      setBusy(false);
    }
  }, [serviceId, stopPolling]);

  const startHealth = useCallback(async () => {
    if (!serviceId.trim()) { setError('Enter the Vocus NBN Service ID first.'); return; }
    setError(null);
    setBusy(true);
    stopPolling();
    setRun(null);
    setDetection(null);
    try {
      const res = await callDiagnostics({ mode: 'RUN_HEALTH', serviceId: serviceId.trim() });
      setRun({ kind: 'health', id: res.healthId, label: 'Service Health Summary', state: res.state, complete: false, startedAt: Date.now() });
      poll('health', res.healthId, 'Service Health Summary');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [serviceId, poll, stopPolling]);

  const safeTests = tests.filter((t) => !t.destructive);
  const riskyTests = tests.filter((t) => t.destructive);

  return (
    <div className="p-6 text-[#ebdbb2] max-w-6xl">
      <div className="flex items-center gap-3 mb-1">
        <FaStethoscope className="text-[#83a598] text-xl" />
        <h1 className="text-2xl font-bold">Service Diagnostics</h1>
      </div>
      <p className="text-[#a89984] text-sm mb-6">
        nbn line tests and Service Health Summary, direct from Vocus. Results are not stored —
        Vocus discards them, and so does this page on reload.
      </p>

      {/* ── Service selector ─────────────────────────────────────────── */}
      <div className="bg-[#32302f] border border-[#504945] rounded-lg p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_auto] gap-3 items-end">
          <label className="block">
            <span className="block text-xs text-[#a89984] mb-1">Vocus NBN Service ID</span>
            <input
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              placeholder="e.g. 12345678"
              className="w-full bg-[#282828] border border-[#504945] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#83a598]"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-[#a89984] mb-1">Access technology</span>
            <select
              value={tech}
              onChange={(e) => setTech(e.target.value)}
              className="w-full bg-[#282828] border border-[#504945] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#83a598]"
            >
              <option value="">All (unfiltered)</option>
              {TECHS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <button
              onClick={startHealth}
              disabled={busy || !serviceId.trim()}
              className="h-[38px] px-4 rounded bg-[#458588] hover:bg-[#83a598] disabled:opacity-40 disabled:cursor-not-allowed text-[#fbf1c7] text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <FaHeartbeat /> Health
            </button>
            <button
              onClick={() => runOperLog('auth')}
              disabled={busy || !serviceId.trim()}
              title="PPPoE authentication attempts — Vocus keeps the last 48 hours"
              className="h-[38px] px-3 rounded bg-[#504945] hover:bg-[#665c54] disabled:opacity-40 disabled:cursor-not-allowed text-[#ebdbb2] text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <FaKey /> Auth log
            </button>
            <button
              onClick={() => runOperLog('session')}
              disabled={busy || !serviceId.trim()}
              title="Connect/disconnect history — last 7 days"
              className="h-[38px] px-3 rounded bg-[#504945] hover:bg-[#665c54] disabled:opacity-40 disabled:cursor-not-allowed text-[#ebdbb2] text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <FaHistory /> Session log
            </button>
          </div>
        </div>
        <p className="text-xs text-[#928374] mt-2">
          Picking the technology filters the menu to the tests Vocus will actually accept for it.
        </p>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-2 bg-[#cc241d]/15 border border-[#cc241d]/50 rounded px-3 py-2 text-sm text-[#fb4934]">
          <FaExclamationTriangle className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Destructive confirmation ─────────────────────────────────── */}
      {confirming && (
        <div className="mb-5 bg-[#282828] border-2 border-[#fb4934] rounded-lg p-4">
          <div className="flex items-center gap-2 text-[#fb4934] font-semibold mb-2">
            <FaExclamationTriangle /> This will interrupt the customer&apos;s service
          </div>
          <p className="text-sm text-[#ebdbb2] mb-1">
            <span className="font-medium">{confirming.name}</span> on service{' '}
            <span className="font-mono text-[#fabd2f]">{serviceId}</span>.
          </p>
          <p className="text-sm text-[#a89984] mb-4">{confirming.blurb}</p>
          <div className="flex gap-2">
            <button
              onClick={() => startTest(confirming, true)}
              className="px-4 py-2 rounded bg-[#cc241d] hover:bg-[#fb4934] text-[#fbf1c7] text-sm font-medium transition-colors"
            >
              Run it — I&apos;ve told the customer
            </button>
            <button
              onClick={() => setConfirming(null)}
              className="px-4 py-2 rounded bg-[#504945] hover:bg-[#665c54] text-[#ebdbb2] text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Live result ──────────────────────────────────────────────── */}
      {run && (
        <div className="mb-5 bg-[#32302f] border border-[#504945] rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-3">
              <span className="font-semibold">{run.label}</span>
              <span className="font-mono text-xs text-[#928374]">{run.id}</span>
              {run.endDateTime && <span className="text-xs text-[#928374]">{fmtDuration(run.startDateTime, run.endDateTime)}</span>}
            </div>
            {run.complete
              ? <Badge tone={run.state === 'failed' ? 'red' : 'green'}>
                  {run.state === 'failed' ? <FaTimesCircle /> : <FaCheckCircle />} {run.state}
                </Badge>
              : run.stalled
                ? <Badge tone="amber"><FaExclamationTriangle /> still running after 5 min — check the Vocus portal</Badge>
                : <Badge tone="blue"><FaSpinner className="animate-spin" /> {run.state || 'polling'}…</Badge>}
          </div>

          {run.pollError && <div className="mb-3 text-sm text-[#fb4934]">Polling stopped: {run.pollError}</div>}

          {/* ── A diagnostic: lead with the verdict ── */}
          {run.kind === 'test' && run.complete && run.summary && (
            <>
              <div className="flex items-center gap-3 flex-wrap mb-3">
                {run.summary.outcome
                  ? <Badge tone={verdictTone(run.summary.outcome)}>
                      {verdictTone(run.summary.outcome) === 'green' ? <FaCheckCircle /> : <FaTimesCircle />} {run.summary.outcome}
                    </Badge>
                  : <Badge tone="grey">no verdict reported</Badge>}
                {run.summary.ranOk && <span className="text-xs text-[#928374]">test status: {run.summary.ranOk}</span>}
              </div>
              {run.summary.notes && (
                <div className="mb-3 flex items-start gap-2 bg-[#d79921]/10 border border-[#d79921]/40 rounded px-3 py-2 text-sm text-[#ebdbb2]">
                  <FaExclamationTriangle className="text-[#fabd2f] mt-0.5 shrink-0" />
                  <span>{run.summary.notes}</span>
                </div>
              )}
              {run.summary.entities.map((e, i) => (
                <GroupCard key={i} title={e.type} subtitle={e.name !== e.type ? e.name : null} items={e.items} />
              ))}
              {run.summary.entities.length === 0 && (
                <div className="text-sm text-[#a89984]">Vocus returned no detail for this test.</div>
              )}
            </>
          )}

          {/* ── Service Health: traffic lights, then what to act on ── */}
          {run.kind === 'health' && run.summary && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {['connectivity', 'performance', 'stability'].map((k) => (
                  <div key={k} className="bg-[#282828] border border-[#3c3836] rounded p-3">
                    <div className="text-xs text-[#a89984] uppercase tracking-wide mb-1.5">{k}</div>
                    <Badge tone={indicatorTone(run.summary[k])}>{run.summary[k] || 'n/a'}</Badge>
                  </div>
                ))}
              </div>

              {/* The handful that decide what to do next, lifted out of the pile. */}
              {hasHeadline(run.summary.headline) && (
                <div className="mb-4 bg-[#282828] border border-[#3c3836] rounded p-3">
                  <div className="text-xs text-[#a89984] uppercase tracking-wide mb-2">At a glance</div>
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    <Fact label="Service" value={run.summary.headline.serviceState}
                          tone={/^up$/i.test(run.summary.headline.serviceState || '') ? 'green' : 'red'} />
                    <Fact label="Outage now" value={fmtBool(run.summary.headline.outage)}
                          tone={/^false$/i.test(run.summary.headline.outage || '') ? 'green' : 'red'} />
                    <Fact label="Link" value={run.summary.headline.linkSpeed} />
                    <Fact label="Port" value={joinPort(run.summary.headline)} 
                          tone={/^up$/i.test(run.summary.headline.portState || '') ? 'green' : undefined} />
                    <Fact label="Optical" value={run.summary.headline.optical} />
                    <Fact label="Dropouts" value={fmtDropouts(run.summary.headline)} />
                    <Fact label="Since" value={fmtDate(run.summary.headline.since)} />
                  </div>
                </div>
              )}

              {/* Vocus's own categories, kept as categories. */}
              {run.summary.groups.map((g) => (
                <GroupCard key={g.name} title={g.name} items={g.items} collapsed={g.items.length > 8} />
              ))}
            </>
          )}

          {/* ── OPER logs: a record list, already complete on arrival ── */}
          {(run.kind === 'authlog' || run.kind === 'sessionlog') && (
            <>
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <Badge tone={run.count > 0 ? 'blue' : 'grey'}>
                  {run.count} {run.count === 1 ? 'record' : 'records'}
                </Badge>
                {run.window && (
                  <span className="text-xs text-[#928374]">{run.window.startDate} → {run.window.endDate}</span>
                )}
                <span className="text-xs text-[#928374]">{run.retention}</span>
              </div>

              {run.count === 0 ? (
                <div className="bg-[#282828] border border-[#3c3836] rounded px-3 py-3 text-sm text-[#a89984]">
                  {run.kind === 'authlog'
                    ? 'No authentication attempts in the last 48 hours. If the customer says they cannot get online, their router is not even trying — check that it is powered, cabled to UNI-D, and configured for PPPoE.'
                    : 'No sessions in this window. Widen the dates, or check the service is actually active.'}
                </div>
              ) : (
                <RecordTable records={run.records} />
              )}
            </>
          )}

          {(run.testMeasure || run.testEntity) && (
            <div className="mt-3">
              <button
                onClick={() => setShowRaw((v) => !v)}
                className="flex items-center gap-1.5 text-sm text-[#83a598] hover:text-[#8ec07c] transition-colors"
              >
                {showRaw ? <FaChevronDown /> : <FaChevronRight />} Full result from Vocus
              </button>
              {showRaw && (
                <div className="mt-2 bg-[#282828] border border-[#3c3836] rounded p-3 max-h-96 overflow-auto">
                  <MeasureTree node={run.testMeasure || run.testEntity} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── What the result told us about the technology ─────────────── */}
      {detection && (detection.tech || detection.candidates?.length > 1) && (
        <div className="mb-4 flex items-start gap-2 bg-[#458588]/10 border border-[#458588]/40 rounded px-3 py-2 text-sm">
          <FaStethoscope className="text-[#83a598] mt-0.5 shrink-0" />
          {detection.tech ? (
            <span className="text-[#ebdbb2]">
              Detected <span className="font-semibold text-[#8ec07c]">{detection.tech}</span> from{' '}
              <span className="text-[#a89984]">{detection.evidence}</span> — menu narrowed to the tests
              Vocus accepts for it. Change the dropdown to override.
            </span>
          ) : (
            <span className="text-[#ebdbb2]">
              Vocus reported <span className="text-[#a89984]">{detection.evidence}</span>, which narrows this to{' '}
              <span className="font-semibold text-[#fabd2f]">{detection.candidates.join(' or ')}</span> but
              does not distinguish them. Pick one in the dropdown to narrow the menu.
            </span>
          )}
        </div>
      )}

      {/* ── Test menu ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">
          Available tests {tech && <span className="text-[#a89984] font-normal">on {tech}</span>}
        </h2>
        <button
          onClick={loadTests}
          disabled={loadingTests}
          className="flex items-center gap-1.5 text-sm text-[#a89984] hover:text-[#ebdbb2] transition-colors"
        >
          <FaSync className={loadingTests ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loadingTests && tests.length === 0 && (
        <div className="text-[#a89984] text-sm flex items-center gap-2"><FaSpinner className="animate-spin" /> Loading…</div>
      )}

      {!loadingTests && tests.length === 0 && !error && (
        <div className="text-[#a89984] text-sm flex items-center gap-2"><FaSearch /> No tests available.</div>
      )}

      {safeTests.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
          {safeTests.map((t) => (
            <div key={t.code} className="bg-[#32302f] border border-[#504945] rounded p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-sm">{t.name}</div>
                <div className="text-xs text-[#a89984] mt-0.5">{t.blurb}</div>
              </div>
              <button
                onClick={() => startTest(t)}
                disabled={busy || !serviceId.trim()}
                className="shrink-0 px-3 py-1.5 rounded bg-[#504945] hover:bg-[#665c54] disabled:opacity-40 disabled:cursor-not-allowed text-[#ebdbb2] text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                <FaPlay className="text-[10px]" /> Run
              </button>
            </div>
          ))}
        </div>
      )}

      {riskyTests.length > 0 && (
        <>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[#fabd2f] mb-2">
            <FaExclamationTriangle /> Service-interrupting
          </h3>
          <p className="text-xs text-[#928374] mb-2">
            These drop the customer&apos;s connection. Tell them before you run one.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {riskyTests.map((t) => (
              <div key={t.code} className="bg-[#32302f] border border-[#d79921]/40 rounded p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-xs text-[#a89984] mt-0.5">{t.blurb}</div>
                </div>
                <button
                  onClick={() => startTest(t)}
                  disabled={busy || !serviceId.trim()}
                  className="shrink-0 px-3 py-1.5 rounded bg-[#cc241d]/30 hover:bg-[#cc241d]/60 border border-[#cc241d]/50 disabled:opacity-40 disabled:cursor-not-allowed text-[#fb4934] text-xs font-medium flex items-center gap-1.5 transition-colors"
                >
                  <FaPlay className="text-[10px]" /> Run
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
